"use client";
import { useState, useEffect } from "react";
import { auth, db } from "../Firebase/firestore";
import {
  ref,
  onValue,
  get,
  set,
  update,
  remove,
  push,
} from "firebase/database";
import {
  createUserWithEmailAndPassword,
  getAuth,
} from "firebase/auth";
import { initializeApp } from "firebase/app";
import { firebaseConfig } from "../Firebase/firestore";
import { FiCheck, FiX, FiLoader, FiEye } from "react-icons/fi";
import "./AdminFirmRequests.css";

const secondaryApp = initializeApp(firebaseConfig, "secondary");
const secondaryAuth = getAuth(secondaryApp);

export default function AdminFirmRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [confirmAction, setConfirmAction] = useState(null); // "approve" | "reject"

  // Load requests + enrich with requester's company name
  useEffect(() => {
    const requestsRef = ref(db, "firmRequests");

    const unsubscribe = onValue(requestsRef, async (snapshot) => {
      if (!snapshot.exists()) {
        setRequests([]);
        setLoading(false);
        return;
      }

      const rawRequests = [];
      snapshot.forEach((child) => {
        const val = child.val();
        if (val.status === "pending") {
          rawRequests.push({
            key: child.key,
            ...val,
          });
        }
      });

      // Collect all unique requestedBy UIDs
      const uids = [...new Set(rawRequests.map((r) => r.requestedBy).filter(Boolean))];

      // Fetch company names
      const companyNames = {};
      if (uids.length > 0) {
        await Promise.all(
          uids.map(async (uid) => {
            const profileRef = ref(db, `profileDashboard/${uid}`);
            const snap = await get(profileRef);
            if (snap.exists()) {
              const data = snap.val();
              companyNames[uid] = data.company_name || data.companyName || data.name || "Unknown";
            } else {
              companyNames[uid] = "Unknown";
            }
          })
        );
      }

      // Enrich requests
      const enriched = rawRequests.map((req) => ({
        ...req,
        requesterCompanyName: companyNames[req.requestedBy] || req.requestedBy.substring(0, 8) + "...",
      }));

      // Sort newest first
      enriched.sort((a, b) => (b.requestedAt || 0) - (a.requestedAt || 0));

      setRequests(enriched);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4800);
  };

  const handleView = (req) => {
    setSelectedRequest(req);
    setRejectReason("");
  };

  const closeModal = () => {
    setSelectedRequest(null);
    setRejectReason("");
  };

  const handleApprove = async () => {
    if (!selectedRequest || actionLoading) return;
    setConfirmAction("approve");
    return;
  };

const handleReject = async () => {
  if (!selectedRequest || actionLoading) return;
  if (!rejectReason.trim()) {
    showMessage("Please enter a rejection reason", "error");
    return;
  }
  setConfirmAction("reject");
  return;
};

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    setActionLoading(true);

    try {
      if (confirmAction === "approve") {
        const {
          dummyEmail,
          dummyPwd,
          requestedBy,
          username,
          company_name,
          phone,
          email,
          gstin,
          pan,
          key: requestKey,
        } = selectedRequest;

        const userCredential = await createUserWithEmailAndPassword(
          secondaryAuth,
          dummyEmail,
          dummyPwd
        );
        const firmUid = userCredential.user.uid;

        await set(ref(db, `profileDashboard/${firmUid}`), {
          type: "firm",
          parentUid: requestedBy,
          username: username?.trim() || "firm-user",
          company_name: company_name?.trim() || "Unnamed Firm",
          phone: phone?.trim() || null,
          email: email?.trim() || null,
          gstin: gstin?.trim() || null,
          pan: pan?.trim() || null,
          createdAt: new Date().toISOString(),
          status: "active",
        });

        await set(ref(db, `profileDashboard/${requestedBy}/firms/${firmUid}`), {
          uid: firmUid,
          company_name: company_name?.trim() || "Unnamed Firm",
          addedAt: Date.now(),
        });

        await remove(ref(db, `firmRequests/${requestKey}`));

        const notifRef = push(ref(db, `user_notifications/Firm Request/${requestedBy}`));
        await set(notifRef, {
          title: "Firm Account Approved",
          message: `Your firm "${company_name}" has been successfully created and is now active.`,
          type: "success",
          timestamp: new Date().toISOString(),
          read: false,
          gifUrl: "/Images/New notification.gif",
        });

        showMessage("Firm created successfully!", "success");
      }

      if (confirmAction === "reject") {
        const { key: requestKey, requestedBy, company_name } = selectedRequest;

        await remove(ref(db, `firmRequests/${requestKey}`));

        const notifRef = push(ref(db, `user_notifications/Firm Request/${requestedBy}`));
        await set(notifRef, {
          title: "Firm Request Rejected",
          message: `Your request to create firm "${company_name}" was not approved.\nReason: ${rejectReason.trim()}`,
          type: "error",
          timestamp: new Date().toISOString(),
          read: false,
          gifUrl: "/Images/New notification.gif",
        });

        showMessage("Request rejected", "success");
      }

      setConfirmAction(null);
      closeModal();
    } catch (err) {
      console.error(err);
      showMessage(err.message, "error");
    } finally {
      setActionLoading(false);
    }
  };
  return (
    <div className="admin-firm-requests-container">
      <h1>Admin – Firm Creation Requests</h1>

      {message.text && (
        <div className={`admin-message ${message.type}`}>{message.text}</div>
      )}

      {loading ? (
        <div className="loading">Loading requests...</div>
      ) : requests.length === 0 ? (
        <div className="empty-state">
          <p>No pending firm creation requests.</p>
        </div>
      ) : (
        <div className="requests-table-wrapper">
          <table className="requests-table">
            <thead>
              <tr>
                <th>Requested By (Company)</th>
                <th>Firm Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Requested</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.key}>
                  <td title={req.requestedBy}>{req.requesterCompanyName}</td>
                  <td>{req.company_name || "—"}</td>
                  <td>{req.username || "—"}</td>
                  <td>{req.email || "—"}</td>
                  <td>{req.phone || "—"}</td>
                  <td>
                    {req.requestedAt
                      ? new Date(req.requestedAt).toLocaleString("en-IN", {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })
                      : "—"}
                  </td>
                  <td>
                    <button
                      className="btn-view"
                      onClick={() => handleView(req)}
                      title="Review request"
                    >
                      <FiEye /> Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal - remains mostly the same, but you can also show requesterCompanyName here if you want */}
      {selectedRequest && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close-icon" onClick={closeModal}>✕</button>
            <h2>Review Firm Request</h2>
            <div className="request-details">
              <p><strong>Requested by (Company):</strong> {selectedRequest.requesterCompanyName}</p>
              <p><strong>Requested by UID:</strong> {selectedRequest.requestedBy}</p>
              <p><strong>Firm name:</strong> {selectedRequest.company_name}</p>
              <p><strong>Username:</strong> {selectedRequest.username}</p>
              <p><strong>Email:</strong> {selectedRequest.email}</p>
              <p><strong>Phone:</strong> {selectedRequest.phone}</p>
              <p><strong>GSTIN:</strong> {selectedRequest.gstin || "—"}</p>
              <p><strong>PAN:</strong> {selectedRequest.pan || "—"}</p>
              <p><strong>Internal dummy email:</strong> {selectedRequest.dummyEmail}</p>
              <hr />
              <small>
                Requested: {new Date(selectedRequest.requestedAt).toLocaleString()}
              </small>
            </div>

            <div className="actions-section">
              <div className="approve-section">
                <button
                  className="btn-approve"
                  onClick={handleApprove}
                  disabled={actionLoading}
                >
                  {actionLoading ? <FiLoader className="spin" /> : <FiCheck />}
                  Approve & Create Firm
                </button>
              </div>

              <div className="reject-section">
                <textarea
                  placeholder="Rejection reason (required)"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                />
                <button
                  className="btn-reject"
                  onClick={handleReject}
                  disabled={actionLoading || !rejectReason.trim()}
                >
                  <FiX /> Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: "400px", textAlign: "center" }}>
            <button className="modal-close-icon" onClick={() => setConfirmAction(null)}>✕</button>
            <h3>
              {confirmAction === "approve"
                ? "Confirm Approval"
                : "Confirm Rejection"}
            </h3>
            <p>
              Are you sure you want to{" "}
              {confirmAction === "approve" ? "approve this firm?" : "reject this request?"}
            </p>

            <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "20px" }}>
              <button className="btn-approve" onClick={handleConfirmAction} disabled={actionLoading}>
                {actionLoading ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}